// React adapter hooks для upload-error UI. Pure mappers — в api/error.ts (TS),
// этот файл держит JSX (ToastAction), поэтому .tsx обязателен.
// Source of truth: docs/specs/sprint-4/stage-4-frontend.md §6.3.

import { useCallback } from 'react';
import { useLocation } from 'wouter';
import type { AxiosError } from 'axios';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { mapPollingFailure, mapUploadError } from '@/api/error';
import type { UploadApiError, UploadFailureReason } from '@/api/types';

export function useUploadErrorToast(): (err: AxiosError<UploadApiError>) => void {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  return useCallback(
    (err: AxiosError<UploadApiError>) => {
      const spec = mapUploadError(err);
      toast({
        title: spec.title,
        description: spec.description,
        variant: 'destructive',
        // Ключ схлопывания — от машинной причины (`kind`), а не от заголовка:
        // «Ошибка» — частая подпись у нескольких несвязанных отказов, и склеивать
        // их было бы неверно. Тост с кнопкой не схлопывается в любом случае
        // (см. canDedupe): действие относится к конкретному файлу.
        dedupeKey: `upload:${spec.kind}`,
        action: spec.action ? (
          <ToastAction altText={spec.action.label} onClick={() => setLocation(spec.action!.href)}>
            {spec.action.label}
          </ToastAction>
        ) : undefined,
      });
    },
    [toast, setLocation],
  );
}

export function usePollingFailureToast(): (reason: UploadFailureReason) => void {
  const { toast } = useToast();
  return useCallback(
    (reason: UploadFailureReason) => {
      const spec = mapPollingFailure(reason);
      // Именно здесь схлопывание и нужно больше всего: при пакете отказ
      // повторяется по файлу, и без ключа человек отсидел бы минуту
      // одинаковых плашек.
      toast({
        title: spec.title,
        description: spec.description,
        variant: 'destructive',
        dedupeKey: `polling:${spec.kind}`,
      });
    },
    [toast],
  );
}
